import { Component, ContentChild, EventEmitter, forwardRef, Input, OnInit, Output, TemplateRef } from '@angular/core';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { MenuItem, MessageService } from 'primeng/api';
import { AbstractView, MessageErrorInterface, SelectMenuItem } from '@core/interfaces';
import { InputPresenter } from './input.presenter';
import { InputViewPresenter } from './interfaces';
import { SharedModule } from '@shared/shared.module';
import { InputNumberInputEvent, InputNumberModule } from 'primeng/inputnumber';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputGroupModule } from 'primeng/inputgroup';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { Observable, of } from 'rxjs';
import { InputTextModule } from 'primeng/inputtext';
import { isErrorMessages } from '@core/interfaces/message-error.interface';
import { DropdownChangeEvent, DropdownModule } from 'primeng/dropdown';
import { InputTypeEnum } from './enums';
import { InputMaskModule } from 'primeng/inputmask';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';
import { CalendarModule } from 'primeng/calendar';
import { CalendarEnum } from '@core/enums';
import { CheckboxModule } from 'primeng/checkbox';
import { InputSwitchChangeEvent, InputSwitchModule } from 'primeng/inputswitch';
import { SkeletonModule } from 'primeng/skeleton';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Toggle } from '../table/interfaces';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { TooltipModule } from 'primeng/tooltip';
import { Store } from '@ngrx/store';
import { PhoneMaskDirective } from './directives';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [
    SharedModule,
    ButtonModule,
    FormsModule,
    ReactiveFormsModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    DropdownModule,
    InputMaskModule,
    NgxMaterialTimepickerModule,
    CalendarModule,
    CheckboxModule,
    InputSwitchModule,
    SkeletonModule,
    RadioButtonModule,
    InputTextareaModule,
    TooltipModule,
    PhoneMaskDirective,
  ],
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
  providers: [
    InputPresenter,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent<T>
  extends AbstractView<InputPresenter<T>>
  implements InputViewPresenter<T>, ControlValueAccessor, OnInit
{
  @Input() id!: string|undefined;
  @Input() name: string = '';
  @Input() type: InputTypeEnum = InputTypeEnum.Number;
  @Input() label: string | null = null;
  @Input() skeleton: boolean = false;
  @Input() readonly: boolean = false;
  @Input() disabled: boolean = false;
  @Input() timePicker: boolean = false;
  @Input() placeholder: string | undefined;
  @Input() prefix: string | undefined;
  @Input() suffix: string | undefined;
  @Input() mode: string = 'decimal';
  @Input() currency: string = 'USD';
  @Input() locale: string = 'en-US';
  @Input() min: number | null = null;
  @Input() max: number | null = null;
  @Input() mask: string | undefined = '000000';
  @Input() maxFractionDigits: number | undefined;
  @Input() useGrouping: boolean = false;
  @Input() floatLabel: boolean = false;
  @Input() errorMessages?: MessageErrorInterface;
  @Input() control?: AbstractControl | null;
  @Input() controlId?: string;
  @Input() dropdownData$: Observable<MenuItem[]> = of([]);
  @Input() dropdownShowClear: boolean = false;
  @Input() dropdownSearch: boolean = false;
  @Input() timePickerFormat: number = 24;
  @Input() timePickerMin: string = '00:00';
  @Input() timePickerMax: string = '23:59';
  @Input() calendarMode: CalendarEnum = CalendarEnum.Single;
  @Input() calendarFormat: string = 'dd/mm/yy';
  @Input() data$: SelectMenuItem<T>[] | Observable<SelectMenuItem<T>[]> = [];
  @Input() switchData!: T;
  @Input() switchName: string = '';
  @Input() containerClass: string = '';
  @Input() contentClass: string = 'block relative';
  @Input() isRadioFlex: boolean = true;
  @Input() appendTo: string = 'body';
  @Input() loading$: Observable<boolean> = of(false);
  @Input() rows: number = 5;
  @Input() cols: number = 30;
  @Input() customIcon?: string;
  @Input() tooltip?: string;
  @Input() autocomplete?: string = '';
  @Input() descriptionLabel?: string | null;

  @Output() dropdownChange = new EventEmitter<string>();
  @Output() calendarChange = new EventEmitter<Date>();
  @Output() switchChange = new EventEmitter<Toggle<T>>();
  @Output() elementChange = new EventEmitter<T|MenuItem>();
  @Output() inputChange = new EventEmitter<string |number | null | undefined>();
  @Output() update = new EventEmitter<string|number|null>();

  uniqueId = `${this.id ?? ''}${Math.random().toString(36).substring(2, 9)}`; 

  internalControl: FormControl;

  @ContentChild('dropdownFooter', { static: false }) footerTemplate?: TemplateRef<unknown>;

  constructor(
    messageService: MessageService,
    router: Router,
    store: Store,
    presenter: InputPresenter<T>,
  ) {
    super(messageService, router, store, presenter);
    this.internalControl = new FormControl(this.value);
  }

  ngOnInit(): void {
    this.presenter.setView(this);
  }

  selectAllText(e: Event): void {
    this.presenter.selectAllText(e);
  }

  // Propiedad enlazada a [(ngModel)]
  private _value: string|number|null = '';

  // Callbacks para ControlValueAccessor
  private onChange: (_: string|number|null) => void = () => {};
  private onTouched: () => void = () => {};

  get value(): string|number|null {
    return this._value;
  }

  set value(value: string|number|null) {
    this._value = value;
    this.onChange(value); // Notificamos cambios
    this.update.emit(value);
  }

  // Implementación de ControlValueAccessor
  writeValue(value: string): void {
    this._value = value;
  }

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  get formControl(): FormControl {
    return this.control as FormControl;
  }

  get isInvalid(): boolean {
    return (
      (this.control?.invalid) ||
      false
    );
  }

  onDropdownChange(e: DropdownChangeEvent, data: Observable<MenuItem[]>): void {
    this.presenter.onDropdownChange(e, data);
  }

  onCalendarChange(e: Date): void {
    this.presenter.onCalendarChange(e);
  }

  onSwitchChange(e: InputSwitchChangeEvent, data: T): void {
    this.presenter.onSwitchChange(e, data);
  }

  onElementChange(e: T|MenuItem): void {
    this.presenter.onElementChange(e);
  }

  onInputChange(e: InputNumberInputEvent): void {
    this.presenter.onInputChange(e);
  }

  isObservable(data: unknown): data is Observable<SelectMenuItem<T>[]> {
    return data instanceof Observable;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isSelectMenuItem(data: any): data is SelectMenuItem<T> {
    return data && typeof data === 'object' && 'label' in data && 'value' in data;
  }

  getErrorMessage(control: FormControl): string|null {
    if (control) {
      const { errors } = control;
  
      if (errors) {
        const errorsList = Object.entries(errors);

        // Extraer la primera entrada de errores
        const [errorKey, errorValue] = errorsList.length > 0 ? Object.entries(errors)[0] : []; 

        if (errorKey && errorValue && this.errorMessages) {
          const controlId = this.controlId as string; // Asegúrate de que controlId sea una cadena
          
          if (controlId && isErrorMessages(this.errorMessages)) {
            // Si this.errorMessages es un ErrorMessages, accedemos a él de forma segura
            const controlErrorMessages = this.errorMessages[controlId];
  
            if (isErrorMessages(controlErrorMessages)) {
              if (controlErrorMessages[errorKey]) {
                const errorMessage = controlErrorMessages[errorKey];
  
                if (typeof errorMessage === 'string') {
                  return errorMessage;
                } else if (typeof errorMessage === 'function') {
                  return errorMessage(errors);
                }
              }
            }
          }
        }
      }
    }

    return null;
  }
}
